import React, { useEffect, useState } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import {
  ArrowDownIcon,
  ArrowRightCircleIcon,
  ArrowLeftCircleIcon,
  ArrowUpIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SunIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import Shifts from './shifts';
import { VictoryBar, VictoryChart, VictoryAxis, VictoryGroup, VictoryLegend, VictoryTooltip, VictoryStack, VictoryArea } from 'victory';

const ShiftBooking = () => {
  const [staff, setStaff] = useState([]);
  const [shiftDetails, setShiftDetails] = useState([]);
  const [holidayShifts, setHolidayShifts] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [showShifts, setShowShifts] = useState(false);
  const [bookedShifts, setBookedShifts] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [bankHolidays, setBankHolidays] = useState({});
  const [expandedStaff, setExpandedStaff] = useState(new Set());
  const [openPatternPicker, setOpenPatternPicker] = useState(null);
  const [activeChart, setActiveChart] = useState('week');
  const [activeDraftDay, setActiveDraftDay] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [allShifts, setAllShifts] = useState([]);
  const [showNextWeekAlert, setShowNextWeekAlert] = useState(false);

  const navigate = useNavigate();

  // Helper function to check if a date is a bank holiday
  const getBankHolidayInfo = (date) => {
    const dateStr = format(new Date(date), 'yyyy-MM-dd');
    const divisions = ['england-and-wales', 'scotland', 'northern-ireland'];
    const holidayDivisions = [];

    divisions.forEach(division => {
      if (bankHolidays[division]?.events) {
        const holiday = bankHolidays[division].events.find(event => event.date === dateStr);
        if (holiday) {
          holidayDivisions.push(division === 'england-and-wales' ? 'England & Wales' : 
                              division === 'scotland' ? 'Scotland' : 'Northern Ireland');
        }
      }
    });

    return holidayDivisions;
  };

  const fetchStaff = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/staffmanager', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch staff');
      const data = await response.json();
      setStaff(data.data);
      setPolicies(data.policies);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchShifts = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/shifts', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch shifts');
      const data = await response.json();

      setAllShifts(data.data || []);

      const startOfSelectedWeek = addDays(
        startOfWeek(new Date(), { weekStartsOn: 1 }),
        weekOffset * 7
      );
      const endOfSelectedWeek = addDays(startOfSelectedWeek, 6);

      const startStr = format(startOfSelectedWeek, 'yyyy-MM-dd');
      const endStr = format(endOfSelectedWeek, 'yyyy-MM-dd');
      const selectedWeekShifts = data.data.filter((shift) => {
        const shiftStr = format(new Date(shift.ShiftDate), 'yyyy-MM-dd');
        return shiftStr >= startStr && shiftStr <= endStr;
      });

      setBookedShifts(selectedWeekShifts);
      setHolidayShifts(data.data.filter((shift) => shift.Holiday === 1));
      setBankHolidays(data.bankHolidays || {});
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [weekOffset]);

  useEffect(() => {
    const nextWeekStart = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 7);
    const nextWeekEnd = addDays(nextWeekStart, 6);
    const startStr = format(nextWeekStart, 'yyyy-MM-dd');
    const endStr = format(nextWeekEnd, 'yyyy-MM-dd');

    const nextWeekShifts = allShifts.filter((shift) => {
      const shiftDate = format(new Date(shift.ShiftDate), 'yyyy-MM-dd');
      return shiftDate >= startStr && shiftDate <= endStr;
    });

    setShowNextWeekAlert(nextWeekShifts.length === 0);
  }, [allShifts]);

  useEffect(() => {
    if (staff.length > 0) {
      const startOfSelectedWeek = addDays(
        startOfWeek(new Date(), { weekStartsOn: 1 }),
        weekOffset * 7
      );

      setShiftDetails((prev) =>
        staff.map((staffMember) => {
          const rolePolicy = policies.find((policy) => policy.Name === staffMember.Role);
          const breakLength = rolePolicy ? rolePolicy.BreakLength : 0;
          const existing = prev.find((p) => p.staffId === staffMember.id);

          return {
            staffId: staffMember.id,
            contractedDays: staffMember.ContractedDays,
            contractedHours: staffMember.ContractedHours,
            breakLength,
            name: staffMember.Name,
            role: staffMember.Role,
            maternity: staffMember.Maternity === 1,
            hourlyRate: staffMember.HourlyRate,
            shifts: Array.from({ length: 7 }, (_, dayIndex) => {
              const date = format(addDays(startOfSelectedWeek, dayIndex), 'yyyy-MM-dd');
              const existingShift = existing?.shifts?.[dayIndex];
              const preserved = existingShift?.date === date ? existingShift : null;
              return {
                date,
                startTime: preserved?.startTime || '',
                endTime: preserved?.endTime || '',
              };
            }),
          };
        })
      );
    }
  }, [staff, weekOffset, policies]);

  const handleChange = (staffIndex, dayIndex, e) => {
    const { name, value } = e.target;
    const updatedShifts = [...shiftDetails];
    updatedShifts[staffIndex].shifts[dayIndex][name] = value;
    setShiftDetails(updatedShifts);
  };

  const handlePublish = async (staffMember) => {
    let firstShiftDate = null;
    let lastShiftDate = null;
    let anyPublished = false;

    for (const shift of staffMember.shifts) {
      if (shift.startTime && shift.endTime) {
        try {
          const response = await fetch('https://api.insightos.co.uk/api/shifts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              StaffId: staffMember.staffId,
              Name: staffMember.name,
              ShiftDate: shift.date,
              StartTime: shift.startTime,
              EndTime: shift.endTime,
            }),
          });
          if (!response.ok) throw new Error('Failed to save shift');

          if (!firstShiftDate) firstShiftDate = shift.date;
          lastShiftDate = shift.date;
          anyPublished = true;

          await response.json();
        } catch (error) {
          console.error('Error saving shift:', error);
        }
      }
    }

    if (anyPublished) {
      setSuccessMessage('Shifts saved successfully for ' + staffMember.name);
      setTimeout(() => setSuccessMessage(''), 3000);

      setShiftDetails((prev) =>
        prev.map((sd) =>
          sd.staffId === staffMember.staffId
            ? { ...sd, shifts: sd.shifts.map((s) => ({ ...s, startTime: '', endTime: '' })) }
            : sd
        )
      );

      fetchShifts();
      fetchStaff();
    }

    if (firstShiftDate) {
      try {
        const response2 = await fetch('https://api.insightos.co.uk/api/emailshifts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            StaffId: staffMember.staffId,
            ShiftDate: firstShiftDate,
            LastShiftDate: lastShiftDate,
          }),
        });

        if (!response2.ok) throw new Error('Failed to send email notification');
      } catch (error) {
        console.error('Error sending email notification:', error);
      }
    }
  };

  const isHoliday = (staffId, date) =>
    holidayShifts.some(
      (shift) =>
        shift.StaffId === staffId &&
        format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === date &&
        shift.Holiday === 1
    );

  const hasShift = (staffId, date) =>
    bookedShifts.find(
      (shift) =>
        shift.StaffId === staffId &&
        format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === date
    );

  const isHalfDay = (staffId, date) =>
    holidayShifts.some(
      (shift) =>
        shift.StaffId === staffId &&
        format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === date &&
        shift.HolidayHalfDay === 1 &&
        shift.StartTime &&
        shift.EndTime
    );

  const calculateShiftHours = (startTime, endTime, staffRole = null) => {
    if (!startTime || !endTime) return 0;
    try {
      const startDate = new Date();
      const endDate = new Date();
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      startDate.setHours(startHours, startMinutes, 0, 0);
      endDate.setHours(endHours, endMinutes, 0, 0);
      if (endDate < startDate) {
        endDate.setDate(endDate.getDate() + 1);
      }
      
      let hoursWorked = (endDate - startDate) / (1000 * 60 * 60);
      
      if (hoursWorked >= 6 && staffRole) {
        const rolePolicy = policies.find((policy) => policy.Name === staffRole);
        const breakLength = rolePolicy ? parseFloat(rolePolicy.BreakLength || 0) : 0;
        hoursWorked -= breakLength / 60;
      }
      
      return parseFloat(hoursWorked.toFixed(2));
    } catch (error) {
      console.error('Error calculating shift hours:', error);
      return 0;
    }
  };

  const calculateWeeklyBreakMinutes = (staffMember) => {
    let totalBreakMinutes = 0;
    const rolePolicy = policies.find((policy) => policy.Name === staffMember.role);
    const breakLength = rolePolicy ? parseFloat(rolePolicy.BreakLength || 0) : 0;
    
    if (breakLength === 0) return 0;
    
    staffMember.shifts.forEach(shift => {
      if (shift.startTime && shift.endTime) {
        const shiftHours = calculateShiftHours(shift.startTime, shift.endTime);
        if (shiftHours >= 6) {
          totalBreakMinutes += breakLength;
        }
      }
    });
    
    const startOfSelectedWeek = addDays(
      startOfWeek(new Date(), { weekStartsOn: 1 }),
      weekOffset * 7
    );
    const endOfSelectedWeek = addDays(startOfSelectedWeek, 6);
    const startStr = format(startOfSelectedWeek, 'yyyy-MM-dd');
    const endStr = format(endOfSelectedWeek, 'yyyy-MM-dd');
    
    const staffShiftsThisWeek = bookedShifts.filter((shift) =>
      shift.StaffId === staffMember.staffId &&
      (() => {
        const shiftStr = format(new Date(shift.ShiftDate), 'yyyy-MM-dd');
        return shiftStr >= startStr && shiftStr <= endStr;
      })()
    );
    
    staffShiftsThisWeek.forEach(shift => {
      if (shift.StartTime && shift.EndTime) {
        const shiftHours = calculateShiftHours(shift.StartTime, shift.EndTime);
        if (shiftHours >= 6) {
          totalBreakMinutes += breakLength;
        }
      }
    });
    
    return totalBreakMinutes;
  };

  const calculateWeeklyHours = (staffMember) => {
    const formHours = staffMember.shifts.reduce((total, shift) => {
      return total + calculateShiftHours(shift.startTime, shift.endTime, staffMember.role);
    }, 0);
    const existingHours = calculateExistingShiftHours(staffMember.staffId, staffMember.role);
    return parseFloat((formHours + existingHours).toFixed(2));
  };

  const calculateDailyCost = (date) => {
    let dailyCost = 0;
    shiftDetails.forEach(staffMember => {
      const shift = staffMember.shifts.find(s => s.date === date);
      if (shift && shift.startTime && shift.endTime) {
        const hours = calculateShiftHours(shift.startTime, shift.endTime, staffMember.role);
        dailyCost += hours * staffMember.hourlyRate;
      }
    });
    bookedShifts.forEach(shift => {
      if (format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === date) {
        const staffMember = staff.find(s => s.id === shift.StaffId);
        if (staffMember && shift.StartTime && shift.EndTime) {
          const hours = calculateShiftHours(shift.StartTime, shift.EndTime, staffMember.Role);
          dailyCost += hours * staffMember.HourlyRate;
        }
      }
    });
    return dailyCost.toFixed(2);
  };

  const calculateExistingShiftHours = (staffId, staffRole) => {
    const startOfSelectedWeek = addDays(
      startOfWeek(new Date(), { weekStartsOn: 1 }),
      weekOffset * 7
    );
    const endOfSelectedWeek = addDays(startOfSelectedWeek, 6);
    const startStr = format(startOfSelectedWeek, 'yyyy-MM-dd');
    const endStr = format(endOfSelectedWeek, 'yyyy-MM-dd');
    const staffShiftsThisWeek = bookedShifts.filter((shift) =>
      shift.StaffId === staffId &&
      (() => {
        const shiftStr = format(new Date(shift.ShiftDate), 'yyyy-MM-dd');
        return shiftStr >= startStr && shiftStr <= endStr;
      })()
    );
    let totalHours = 0;
    for (const shift of staffShiftsThisWeek) {
      if (shift.StartTime && shift.EndTime) {
        try {
          const startDate = new Date();
          const endDate = new Date();
          const [startHours, startMinutes] = shift.StartTime.split(':').map(Number);
          const [endHours, endMinutes] = shift.EndTime.split(':').map(Number);
          startDate.setHours(startHours, startMinutes, 0, 0);
          endDate.setHours(endHours, endMinutes, 0, 0);
          if (endDate < startDate) {
            endDate.setDate(endDate.getDate() + 1);
          }
          let hoursWorked = (endDate - startDate) / (1000 * 60 * 60);
          
          if (hoursWorked >= 6 && staffRole) {
            const rolePolicy = policies.find((policy) => policy.Name === staffRole);
            const breakLength = rolePolicy ? parseFloat(rolePolicy.BreakLength || 0) : 0;
            hoursWorked -= breakLength / 60;
          }
          
          totalHours += parseFloat(hoursWorked.toFixed(2));
        } catch (error) {
          console.error('Error calculating shift hours:', error);
        }
      }
    }
    return parseFloat(totalHours.toFixed(2));
  };

  const calculateWeeklyDays = (staffMember) => {
    const daysInForm = staffMember.shifts.filter(
      shift => shift.startTime && shift.endTime
    ).length;
    const existingShiftDays = staffMember.shifts.filter(shift =>
      hasShift(staffMember.staffId, shift.date)
    ).length;
    const totalDays = daysInForm + existingShiftDays;
    return parseInt(totalDays, 10);
  };

  const handleCopyShifts = async (startDate) => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/copyshifts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          startDate: format(startDate, 'yyyy-MM-dd'),
        }),
      });
      if (!response.ok) throw new Error('Failed to copy shifts');
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setSuccessMessage('Shifts copied successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchShifts();
      fetchStaff();
      setWeekOffset(weekOffset + 1);
    } catch (error) {
      console.error('Error copying shifts:', error);
    }
  };

  const handleDelete = async (staffId, date) => {
    try {
      const normalizedDate = format(new Date(date), 'yyyy-MM-dd');
      const response = await fetch('https://api.insightos.co.uk/api/deletebookshifts', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ StaffId: staffId, ShiftDate: normalizedDate }),
      });
      if (!response.ok) throw new Error('Failed to delete shift');
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      fetchShifts();
      fetchStaff();
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  };

  const handleNextWeek = () => setWeekOffset(weekOffset + 1);
  const handlePreviousWeek = () => setWeekOffset(Math.max(0, weekOffset - 1));

  const toggleExpand = (staffId) => {
    setExpandedStaff(prev => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  };

  const parseFlexibleWorking = (flexStr) => {
    if (!flexStr) return { days: [], reason: '' };
    const parts = flexStr.split(',').map(s => s.trim()).filter(Boolean);
    const FW_DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
    const lastPart = parts[parts.length - 1] || '';
    const reason = !FW_DAYS.includes(lastPart) ? lastPart : '';
    const days = reason ? parts.slice(0, -1) : parts;
    return { days, reason };
  };

  const getRowColorClass = (staffMember) => {
    if (staffMember.maternity) return 'bg-white';
    const hours = calculateWeeklyHours(staffMember);
    if (hours === 0) return 'bg-red-100';
    if (hours < Number(staffMember.contractedHours || 0)) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  const getPatternsForRole = (roleName) => {
    const roleData = policies.find(p => p.Name === roleName);
    if (!roleData?.ShiftPatterns) return [];
    return roleData.ShiftPatterns.split(',')
      .map(p => p.trim())
      .filter(Boolean)
      .map(p => {
        const dashIdx = p.indexOf('-', p.indexOf(':') + 1);
        const start = dashIdx > 0 ? p.slice(0, dashIdx) : '';
        const end = dashIdx > 0 ? p.slice(dashIdx + 1) : '';
        return { start, end, label: `${p.slice(0, dashIdx)} – ${p.slice(dashIdx + 1)}` };
      })
      .filter(p => p.start && p.end);
  };

  const ROLE_COLORS = [
    '#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#84cc16','#f97316',
  ];

  const getRoleNames = () => [...new Set(staff.map(s => s.Role).filter(Boolean))];

  const getRoleForShift = (shift) => {
    const s = staff.find((st) => st.id === shift.StaffId);
    return s?.Role || '';
  };

  const getStaffNameById = (id) => {
    const s = staff.find((st) => st.id === id);
    return s?.Name || 'Unknown';
  };

  // ── NEW: getCellInfo returns volunteers, declined, replacement info ──
  const getCellInfo = (role, dateStr) => {
    const shifts = allShifts.filter(
      (shift) => format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === dateStr && getRoleForShift(shift) === role
    );
    if (shifts.length === 0) return null;

    const volunteersSet = new Set();
    const declinedSet = new Set();
    let replacementId = null;
    let covered = false;

    shifts.forEach(shift => {
      // Parse StandByVolunteers
      let vols = shift.StandByVolunteers;
      if (typeof vols === 'string') {
        try { vols = JSON.parse(vols); } catch (e) { vols = vols.split(',').map(Number); }
      }
      if (Array.isArray(vols)) {
        vols.forEach(id => volunteersSet.add(Number(id)));
      }

      // Parse Declined
      let dec = shift.Declined;
      if (typeof dec === 'string') {
        try { dec = JSON.parse(dec); } catch (e) { dec = dec.split(',').map(Number); }
      }
      if (Array.isArray(dec)) {
        dec.forEach(id => declinedSet.add(Number(id)));
      }

      // Check ReplacementID (covered shift)
      if (shift.ReplacementID) {
        replacementId = shift.ReplacementID;
        covered = true;
      }
    });

    return {
      volunteers: Array.from(volunteersSet),
      declined: Array.from(declinedSet),
      replacementId,
      covered,
    };
  };

  const shiftCoversHour = (shift, hour) => {
    if (!shift.StartTime || !shift.EndTime) return false;
    const [sh] = shift.StartTime.split(':').map(Number);
    const [eh] = shift.EndTime.split(':').map(Number);
    const sm2 = parseInt(shift.StartTime.split(':')[1] || 0);
    const em2 = parseInt(shift.EndTime.split(':')[1] || 0);
    const startMins = sh * 60 + sm2;
    const endMins = eh * 60 + em2;
    const slotMins = hour * 60;
    if (endMins > startMins) return slotMins >= startMins && slotMins < endMins;
    return slotMins >= startMins || slotMins < endMins;
  };

  const buildWeekData = () => {
    const roleNames = getRoleNames();
    const startOfSelectedWeek = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7);
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(startOfSelectedWeek, i);
      return { date: format(d, 'yyyy-MM-dd'), label: format(d, 'EEE\nMM/dd') };
    });
    const datasets = roleNames.map((role, rIdx) => ({
      role,
      color: ROLE_COLORS[rIdx % ROLE_COLORS.length],
      data: days.map(({ date, label }) => {
        const count = bookedShifts.filter(shift => {
          const s = staff.find(st => st.id === shift.StaffId);
          return s?.Role === role && format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === date;
        }).length;
        return { x: label, y: count };
      }),
    }));
    return { datasets, roleNames, days };
  };

  const buildDayData = (dateStr) => {
    const roleNames = getRoleNames();
    const shiftsOnDay = bookedShifts.filter(
      shift => format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === dateStr
    );
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const hourLabel = h => {
      if (h === 0) return '12am';
      if (h < 12) return `${h}am`;
      if (h === 12) return '12pm';
      return `${h - 12}pm`;
    };
    const datasets = roleNames.map((role, rIdx) => ({
      role,
      color: ROLE_COLORS[rIdx % ROLE_COLORS.length],
      data: hours.map(hour => ({
        x: hourLabel(hour),
        y: shiftsOnDay.filter(shift => {
          const s = staff.find(st => st.id === shift.StaffId);
          return s?.Role === role && shiftCoversHour(shift, hour);
        }).length,
      })),
    }));
    return { datasets, roleNames, hours, hourLabel };
  };

  const draftShiftCoversHour = (startTime, endTime, hour) => {
    if (!startTime || !endTime) return false;
    const parts1 = startTime.split(':').map(Number);
    const parts2 = endTime.split(':').map(Number);
    const startMins = parts1[0] * 60 + (parts1[1] || 0);
    const endMins = parts2[0] * 60 + (parts2[1] || 0);
    const slotMins = hour * 60;
    if (endMins > startMins) return slotMins >= startMins && slotMins < endMins;
    return slotMins >= startMins || slotMins < endMins;
  };

  const buildDraftDayCoverage = (dateStr) => {
    const roleNames = getRoleNames();
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const hourLabel = h => {
      if (h === 0) return '12am';
      if (h < 12) return `${h}am`;
      if (h === 12) return '12pm';
      return `${h - 12}pm`;
    };
    const datasets = roleNames.map((role, rIdx) => ({
      role,
      color: ROLE_COLORS[rIdx % ROLE_COLORS.length],
      data: hours.map(hour => ({
        x: hour,
        y: shiftDetails.filter(sm => {
          if (sm.role !== role) return false;
          const dayShift = sm.shifts.find(s => s.date === dateStr);
          return dayShift && draftShiftCoversHour(dayShift.startTime, dayShift.endTime, hour);
        }).length,
      })),
    }));
    const activeHours = hours.filter(h => datasets.some(ds => ds.data[h]?.y > 0));
    const minHour = activeHours.length ? Math.max(0, activeHours[0] - 1) : 7;
    const maxHour = activeHours.length ? Math.min(23, activeHours[activeHours.length - 1] + 1) : 20;
    const trimmed = datasets.map(ds => ({
      ...ds,
      data: ds.data.filter((_, i) => i >= minHour && i <= maxHour),
    }));
    return { datasets: trimmed, roleNames, hourLabel, minHour, maxHour, hasAny: activeHours.length > 0 };
  };

  return (
    <div className="mt-8 flow-root mt-5 mb-5">
      {showNextWeekAlert && (
        <div className="mt-5 mb-5 flex items-center gap-3 rounded-xl bg-red-500 p-4 shadow-md animate-pulse">
          <ExclamationTriangleIcon className="h-6 w-6 text-white flex-shrink-0" />
          <div className="text-sm text-white">
            <p className="font-semibold">Next week's shifts have not been completed.</p>
            <p>Please publish the shift rota for next week.</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-xl animate-bounce shadow-md mt-5 mb-5">
          <p className="font-bold">Success</p>
          <p>{successMessage}</p>
        </div>
      )}
      <div className="flex flex-wrap justify-start items-center gap-2">
        <button
          onClick={() => navigate('/taskcalendar')}
          type="button"
          className="inline-flex items-center whitespace-nowrap gap-x-1 rounded-md bg-purple-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 sm:gap-x-1.5 sm:px-2.5 sm:py-1.5 sm:text-sm"
        >
          <ClipboardDocumentCheckIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
          Task Manager
        </button>
        {!showShifts && (
          <button
            onClick={() => setShowShifts(true)}
            type="button"
            className="inline-flex items-center whitespace-nowrap gap-x-1 rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 sm:gap-x-1.5 sm:px-2.5 sm:py-1.5 sm:text-sm"
          >
            <ArrowDownIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
            Show Booked Shifts
          </button>
        )}
        <button
          onClick={() =>
            handleCopyShifts(
              addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7)
            )
          }
          type="button"
          data-component="shift-copy"
          
          data-feature="shift-copy-next-week"
          data-time-saved="20"
          className="inline-flex items-center whitespace-nowrap gap-x-1 rounded-md bg-emerald-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 sm:gap-x-1.5 sm:px-2.5 sm:py-1.5 sm:text-sm"
        >
          <ClipboardDocumentCheckIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
          Copy For Next Week
        </button>

        {showShifts && (
          <button
            onClick={() => setShowShifts(false)}
            type="button"
            className="inline-flex items-center whitespace-nowrap gap-x-1 rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 sm:gap-x-1.5 sm:px-2.5 sm:py-1.5 sm:text-sm"
          >
            <ArrowUpIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
            Hide Shifts
          </button>
        )}
      </div>
      <div className="mt-5">
        {showShifts && (
          <div className="overflow-x-auto">
            <Shifts />
          </div>
        )}
      </div>
      {/* ── Mobile card layout (hidden on md+) ───────────────────────── */}
      <div className="block md:hidden space-y-4 mt-4">
        {shiftDetails.map((staffMember, staffIndex) => (
          <div key={staffIndex} className={`${getRowColorClass(staffMember)} rounded-2xl shadow-md border border-gray-200 overflow-hidden`}>
            {/* Card header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-bold text-sm ${staffMember.maternity ? 'text-red-200' : 'text-white'}`}>
                    {staffMember.name}
                    {staffMember.maternity && <span className="ml-2 text-xs text-red-200">(Maternity)</span>}
                  </p>
                  <p className="text-purple-200 text-xs">{staffMember.role}</p>
                </div>
                <button
                  onClick={() => { if (!staffMember.maternity) handlePublish(staffMember); }}
                  disabled={staffMember.maternity}
                  data-component="publish-shifts"
                  data-feature="publish_shifts"
                  data-time-saved="10"
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white ${
                    staffMember.maternity ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-400'
                  }`}
                >
                  Publish
                </button>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-purple-100">
                <span>⏱ {calculateWeeklyHours(staffMember)} / {staffMember.contractedHours}h</span>
                <span>📅 {calculateWeeklyDays(staffMember)} / {staffMember.contractedDays} days</span>
              </div>
            </div>

            {/* Day rows */}
            <div className="divide-y divide-gray-100">
              {staffMember.shifts.map((shift, dayIndex) => {
                const holidayDivisions = getBankHolidayInfo(shift.date);
                const booked = hasShift(staffMember.staffId, shift.date);
                const onHoliday = isHoliday(staffMember.staffId, shift.date);
                const halfDay = isHalfDay(staffMember.staffId, shift.date);
                return (
                  <div key={dayIndex} className="px-4 py-3">
                    <div className="mb-1.5">
                      <p className="text-sm font-semibold text-gray-700">
                        {format(new Date(shift.date), 'EEEE, MMM d')}
                      </p>
                      {holidayDivisions.length > 0 && (
                        <p className="text-xs text-red-600">🏴󠁧󠁢󠁥󠁮󠁧󠁿 Bank Holiday ({holidayDivisions.join(', ')})</p>
                      )}
                      <p className="text-xs text-gray-400">Daily Cost: £{calculateDailyCost(shift.date)}</p>
                    </div>

                    {staffMember.maternity ? (
                      <span className="text-xs text-red-500 font-semibold">Maternity leave — shifts locked</span>
                    ) : onHoliday ? (
                      <span className="text-sm text-orange-500 font-semibold flex items-center gap-1">
                        <SunIcon className="h-4 w-4 text-yellow-400" />
                        {halfDay ? 'Half-Day Holiday' : 'Holiday'}
                      </span>
                    ) : booked ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={Number(booked.Sick) === 1 ? "rounded-lg bg-gradient-to-r from-rose-600 to-red-600 px-3 py-1 text-base font-bold text-white shadow-sm" : "text-sm text-green-600 font-semibold"}>
                          {Number(booked.Sick) === 1 ? 'Sick Today' : `${booked.StartTime} – ${booked.EndTime}`}
                        </span>
                        {Number(booked.Cover) === 1 && (
                          <span className="rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 px-2.5 py-1 text-xs font-bold text-white shadow-sm">
                            Cover
                          </span>
                        )}
                        <button
                          onClick={() => {
                            if (booked.BankHoliday === 1) {
                              if (window.confirm('Holiday allocation will be altered. Are you sure?')) {
                                handleDelete(staffMember.staffId, booked.ShiftDate);
                              }
                            } else {
                              handleDelete(staffMember.staffId, booked.ShiftDate);
                            }
                          }}
                          className="text-xs bg-red-500 text-white rounded px-2 py-0.5 hover:bg-red-600"
                        >
                          Delete
                        </button>
                        {booked.BankHoliday === 1 && (
                          <span className="text-xs text-red-600">🏴󠁧󠁢󠁥󠁮󠁧󠁿 Bank Holiday</span>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            name="startTime"
                            className="border border-gray-300 rounded-lg py-1.5 px-2 text-sm flex-1 min-w-0"
                            value={shift.startTime}
                            onChange={(e) => handleChange(staffIndex, dayIndex, e)}
                          />
                          <span className="text-gray-400 text-xs">–</span>
                          <input
                            type="time"
                            name="endTime"
                            className="border border-gray-300 rounded-lg py-1.5 px-2 text-sm flex-1 min-w-0"
                            value={shift.endTime}
                            onChange={(e) => handleChange(staffIndex, dayIndex, e)}
                          />
                          <button
                            type="button"
                            onClick={() => setOpenPatternPicker(
                              openPatternPicker?.staffIndex === staffIndex && openPatternPicker?.dayIndex === dayIndex
                                ? null
                                : { staffIndex, dayIndex }
                            )}
                            className="text-indigo-400 hover:text-indigo-700 flex-shrink-0"
                            title="Shift patterns"
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </button>
                        </div>
                        {openPatternPicker?.staffIndex === staffIndex && openPatternPicker?.dayIndex === dayIndex && (() => {
                          const patterns = getPatternsForRole(staffMember.role);
                          return patterns.length > 0 ? (
                            <div className="absolute z-20 left-0 mt-1 bg-white border border-indigo-200 rounded-xl shadow-lg p-2 flex flex-wrap gap-1.5 min-w-[180px]">
                              {patterns.map((p, pi) => (
                                <button
                                  key={pi}
                                  type="button"
                                  onClick={() => {
                                    const updatedShifts = [...shiftDetails];
                                    updatedShifts[staffIndex].shifts[dayIndex].startTime = p.start;
                                    updatedShifts[staffIndex].shifts[dayIndex].endTime = p.end;
                                    setShiftDetails(updatedShifts);
                                    setOpenPatternPicker(null);
                                  }}
                                  className="bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-1 rounded-full hover:bg-indigo-200"
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="absolute z-20 left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 text-xs text-gray-500">
                              No patterns set for this role
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Flexible working expand */}
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => toggleExpand(staffMember.staffId)}
                className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 w-full"
              >
                {expandedStaff.has(staffMember.staffId)
                  ? <ChevronUpIcon className="h-3.5 w-3.5" />
                  : <ChevronDownIcon className="h-3.5 w-3.5" />
                }
                Flexible Working
              </button>
              {expandedStaff.has(staffMember.staffId) && (() => {
                const fw = parseFlexibleWorking(staff.find(s => s.id === staffMember.staffId)?.FlexibleWorking);
                return (
                  <div className="mt-2 text-xs text-gray-700 space-y-1">
                    {fw.days.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {fw.days.map(d => (
                          <span key={d} className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{d}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400">No flexible days set</p>
                    )}
                    {fw.reason && <p className="text-gray-500 italic">Reason: {fw.reason}</p>}
                  </div>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      {/* ── Desktop table layout (hidden below md) ──────────────────────── */}
      <div className="hidden md:block -mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="flex items-center justify-center py-2  align-middle sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={handlePreviousWeek}
            className="mr-2 rounded-full bg-red-600 p-1 text-white shadow-sm hover:bg-indigo-500"
          >
            <ArrowLeftCircleIcon aria-hidden="true" className="size-7" />
          </button>
          <span className="text-md font-semibold text-gray-900 p-2 bg-gray-200 rounded-md">
            {format(
              addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7),
              'MMMM dd, yyyy'
            )}
          </span>
          <button
            type="button"
            onClick={handleNextWeek}
            className="rounded-full bg-emerald-500 p-1 ml-2 text-white shadow-sm hover:bg-indigo-500"
          >
            <ArrowRightCircleIcon aria-hidden="true" className="size-7" />
          </button>
        </div>
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-purple-700 sm:pl-0">
                  Name
                </th>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-purple-700 sm:pl-0">
                  Role
                </th>
                {shiftDetails[0]?.shifts.map((shift, dayIndex) => {
                  const holidayDivisions = getBankHolidayInfo(shift.date);
                  return (
                    <th key={dayIndex} className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-blue-500 sm:pl-0">
                      {format(new Date(shift.date), 'EEEE, MMM d')}
                      <br />
                      {holidayDivisions.length > 0 && (
                        <span className="text-red-600 font-medium text-xs">
                          🏴󠁧󠁢󠁥󠁮󠁧󠁿 Bank Holiday ({holidayDivisions.join(', ')})
                        </span>
                      )}
                      <br />
                      <span className="text-gray-500">Daily Cost: £{calculateDailyCost(shift.date)}</span>
                    </th>
                  );
                })}
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-purple-700 sm:pl-0">
                  Total Hours
                </th>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-purple-700 sm:pl-0">
                  Total Days
                </th>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                  Publish
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {shiftDetails.map((staffMember, staffIndex) => (
                <tr key={staffIndex} className={getRowColorClass(staffMember)}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-0">
                    <div className="flex flex-col">
                      <span className={staffMember.maternity ? "text-red-500" : "text-gray-900"}>
                        {staffMember.name}
                      </span>
                      {staffMember.maternity && (
                        <span className="text-xs font-semibold uppercase tracking-wide text-red-500">
                          On maternity leave
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleExpand(staffMember.staffId)}
                        className="mt-1 flex items-center gap-0.5 text-xs text-indigo-500 hover:text-indigo-700"
                      >
                        {expandedStaff.has(staffMember.staffId)
                          ? <ChevronUpIcon className="h-3.5 w-3.5" />
                          : <ChevronDownIcon className="h-3.5 w-3.5" />
                        }
                        Flexible working
                      </button>
                      {expandedStaff.has(staffMember.staffId) && (() => {
                        const fw = parseFlexibleWorking(staff.find(s => s.id === staffMember.staffId)?.FlexibleWorking);
                        return (
                          <div className="mt-1 p-2 bg-indigo-50 rounded-md text-xs text-gray-700 space-y-1 min-w-[160px]">
                            {fw.days.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {fw.days.map(d => (
                                  <span key={d} className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{d}</span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-400">No flexible days set</p>
                            )}
                            {fw.reason && <p className="text-gray-500 italic">Reason: {fw.reason}</p>}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                    {staffMember.role}
                  </td>
                  {staffMember.shifts.map((shift, dayIndex) => (
                    <td key={dayIndex} className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 overflow-visible min-w-0">
                      {staffMember.maternity ? (
                        <span className="text-red-500 font-semibold">Maternity leave — shifts locked</span>
                      ) : isHoliday(staffMember.staffId, shift.date) ? (
                        <span className="text-red-500 p-7 font-semibold">
                          {isHalfDay(staffMember.staffId, shift.date) ? (
                            <>
                              <SunIcon className="h-8 w-8 text-yellow-300 inline-block" />
                              <span className="ml-2">
                                Half-Day Holiday:{' '}
                                {
                                  holidayShifts.find(
                                    (h) =>
                                      h.StaffId === staffMember.staffId &&
                                      format(new Date(h.ShiftDate), 'yyyy-MM-dd') === shift.date &&
                                      h.HolidayHalfDay === 1
                                  )?.StartTime
                                }{' '}
                                -{' '}
                                {
                                  holidayShifts.find(
                                    (h) =>
                                      h.StaffId === staffMember.staffId &&
                                      format(new Date(h.ShiftDate), 'yyyy-MM-dd') === shift.date &&
                                      h.HolidayHalfDay === 1
                                  )?.EndTime
                                }
                              </span>
                            </>
                          ) : (
                            <>
                              <SunIcon className="h-8 w-8 text-yellow-300 inline-block" />
                              Holiday
                            </>
                          )}
                        </span>
                      ) : hasShift(staffMember.staffId, shift.date) ? (
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={Number(hasShift(staffMember.staffId, shift.date).Sick) === 1 ? "rounded-lg bg-gradient-to-r from-rose-600 to-red-600 px-3 py-1 text-base font-bold text-white shadow-sm" : "text-green-600 font-semibold"}>
                              {Number(hasShift(staffMember.staffId, shift.date).Sick) === 1
                                ? 'Sick Today'
                                : `${hasShift(staffMember.staffId, shift.date).StartTime} - ${hasShift(staffMember.staffId, shift.date).EndTime}`}
                            </span>
                            {Number(hasShift(staffMember.staffId, shift.date).Cover) === 1 && (
                              <span className="rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 px-2.5 py-1 text-xs font-bold text-white shadow-sm">
                                Cover
                              </span>
                            )}
                            <button
                              onClick={() => {
                                const bookedShift = hasShift(staffMember.staffId, shift.date);
                                if (bookedShift && bookedShift.BankHoliday === 1) {
                                  if (window.confirm('Holiday allocation will be altered. Are you sure?')) {
                                    handleDelete(staffMember.staffId, bookedShift.ShiftDate);
                                  }
                                } else {
                                  handleDelete(staffMember.staffId, bookedShift.ShiftDate);
                                }
                              }}
                              className="bg-red-500 text-white rounded-md px-2 py-1 hover:bg-blue-600"
                            >
                              Delete
                            </button>
                          </div>
                          {hasShift(staffMember.staffId, shift.date).BankHoliday === 1 && (
                            <div className="text-red-600 font-medium text-xs mt-1">
                              🏴󠁧󠁢󠁥󠁮󠁧󠁿 Bank Holiday
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="flex items-center gap-2 min-w-0">
                            <input
                              type="time"
                              name="startTime"
                              className="border border-gray-300 rounded-md py-1 px-2 min-w-0 w-24 sm:w-28"
                              value={shift.startTime}
                              onChange={(e) => handleChange(staffIndex, dayIndex, e)}
                            />
                            <input
                              type="time"
                              name="endTime"
                              className="border border-gray-300 rounded-md py-1 px-2 min-w-0 w-24 sm:w-28"
                              value={shift.endTime}
                              onChange={(e) => handleChange(staffIndex, dayIndex, e)}
                            />
                            <button
                              type="button"
                              onClick={() => setOpenPatternPicker(
                                openPatternPicker?.staffIndex === staffIndex && openPatternPicker?.dayIndex === dayIndex
                                  ? null
                                  : { staffIndex, dayIndex }
                              )}
                              className="text-indigo-400 hover:text-indigo-700 flex-shrink-0"
                              title="Shift patterns"
                            >
                              <ChevronRightIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {openPatternPicker?.staffIndex === staffIndex && openPatternPicker?.dayIndex === dayIndex && (() => {
                            const patterns = getPatternsForRole(staffMember.role);
                            return patterns.length > 0 ? (
                              <div className="absolute z-20 left-0 mt-1 bg-white border border-indigo-200 rounded-xl shadow-lg p-2 flex flex-wrap gap-1.5 min-w-[180px]">
                                {patterns.map((p, pi) => (
                                  <button
                                    key={pi}
                                    type="button"
                                    onClick={() => {
                                      const updatedShifts = [...shiftDetails];
                                      updatedShifts[staffIndex].shifts[dayIndex].startTime = p.start;
                                      updatedShifts[staffIndex].shifts[dayIndex].endTime = p.end;
                                      setShiftDetails(updatedShifts);
                                      setOpenPatternPicker(null);
                                    }}
                                    className="bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-1 rounded-full hover:bg-indigo-200"
                                  >
                                    {p.label}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="absolute z-20 left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 text-xs text-gray-500">
                                No patterns set for this role
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-emerald-500 sm:pl-0">
                    {calculateWeeklyHours(staffMember)} / {staffMember.contractedHours} hours
                    {(() => {
                      const weeklyBreakMinutes = calculateWeeklyBreakMinutes(staffMember);
                      const rolePolicy = policies.find((policy) => policy.Name === staffMember.role);
                      const breakLength = rolePolicy ? parseFloat(rolePolicy.BreakLength || 0) : 0;
                      return breakLength > 0 ? (
                        <div className="text-gray-500 text-xs mt-1">
                          Break deducted: {weeklyBreakMinutes} mins this week
                          <br />
                          ({breakLength} mins per 6+ hour shift)
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-indigo-600 sm:pl-0">
                    {calculateWeeklyDays(staffMember)} / {staffMember.contractedDays} days
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <button
                      onClick={() => {
                        if (!staffMember.maternity) {
                          handlePublish(staffMember);
                        }
                      }}
                      data-component="publish-shifts"

                      data-feature="publish_shifts"
                      data-time-saved="10"
                      className={`rounded-md px-2 py-1 text-white ${
                        staffMember.maternity
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-emerald-500 hover:bg-blue-600"
                      }`}
                      disabled={staffMember.maternity}
                    >
                      Publish
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Standby Cover Table (with Declined and Replacement) ──────── */}
      <div className="mt-10">
        <h3 className="text-sm font-bold text-gray-800 mb-3">🛟 Standby Cover</h3>
        <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-md">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
                {Array.from({ length: 7 }, (_, i) => {
                  const d = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7 + i);
                  return (
                    <th key={i} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {format(d, 'EEE')}<br /><span className="text-gray-400">{format(d, 'MMM d')}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {getRoleNames().map((role) => (
                <tr key={role} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{role}</td>
                  {Array.from({ length: 7 }, (_, dayIdx) => {
                    const date = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7 + dayIdx);
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const cellInfo = getCellInfo(role, dateStr);
                    if (!cellInfo) {
                      return <td key={dayIdx} className="px-4 py-3 text-sm text-gray-300">—</td>;
                    }
                    const { volunteers, declined, replacementId, covered } = cellInfo;
                    if (covered) {
                      return (
                        <td key={dayIdx} className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                            Covered by {getStaffNameById(replacementId)}
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td key={dayIdx} className="px-4 py-3">
                        {volunteers.length === 0 && declined.length === 0 ? (
                          <span className="text-xs text-gray-400">No volunteers</span>
                        ) : (
                          <div className="space-y-1">
                            {volunteers.map(volId => (
                              <div key={volId} className="flex items-center gap-2">
                                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                                <span className="text-xs text-gray-700">{getStaffNameById(volId)}</span>
                              </div>
                            ))}
                            {declined.map(decId => (
                              <div key={decId} className="flex items-center gap-2">
                                <XCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
                                <span className="text-xs text-gray-400 line-through">{getStaffNameById(decId)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Coverage Charts ───────────────────────────────────────────── */}
      <div className="mt-8">
        {/* Tab strip */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setActiveChart('week')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeChart === 'week'
                ? 'bg-indigo-600 text-white shadow'
                : 'bg-gray-100 text-gray-600 hover:bg-indigo-100'
            }`}
          >
            📅 Week Overview
          </button>
          {(() => {
            const startOfSelectedWeek = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7);
            return Array.from({ length: 7 }, (_, i) => {
              const d = addDays(startOfSelectedWeek, i);
              const dateStr = format(d, 'yyyy-MM-dd');
              const label = format(d, 'EEE d');
              const count = bookedShifts.filter(s => format(new Date(s.ShiftDate), 'yyyy-MM-dd') === dateStr).length;
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => setActiveChart(dateStr)}
                  className={`relative px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    activeChart === dateStr
                      ? 'bg-indigo-600 text-white shadow'
                      : 'bg-gray-100 text-gray-600 hover:bg-indigo-100'
                  }`}
                >
                  {label}
                  {count > 0 && (
                    <span className={`ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-bold ${
                      activeChart === dateStr ? 'bg-white text-indigo-600' : 'bg-indigo-600 text-white'
                    }`}>{count}</span>
                  )}
                </button>
              );
            });
          })()}
        </div>

        {/* Week chart */}
        {activeChart === 'week' && (() => {
          const { datasets, roleNames } = buildWeekData();
          if (!datasets.length) return <p className="text-xs text-gray-400">No booked shifts this week.</p>;
          const maxY = Math.max(1, ...datasets.flatMap(ds => ds.data.map(d => d.y)));
          return (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Weekly Coverage by Role</h3>
                <div className="flex flex-wrap gap-3">
                  {roleNames.map((r, i) => (
                    <span key={r} className="flex items-center gap-1 text-xs text-gray-600">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: ROLE_COLORS[i % ROLE_COLORS.length] }} />
                      {r}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <VictoryChart width={720} height={280} domainPadding={{ x: 30 }}>
                  <VictoryAxis
                    style={{
                      tickLabels: { fontSize: 9, fill: '#6b7280' },
                      axis: { stroke: '#e5e7eb' },
                      grid: { stroke: 'none' },
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    domain={[0, maxY + 1]}
                    tickFormat={t => Number.isInteger(t) ? t : ''}
                    style={{
                      tickLabels: { fontSize: 9, fill: '#6b7280' },
                      axis: { stroke: '#e5e7eb' },
                      grid: { stroke: '#f3f4f6', strokeDasharray: '4' },
                    }}
                    label="Staff"
                  />
                  <VictoryStack>
                    {datasets.map((ds, i) => (
                      <VictoryBar
                        key={i}
                        data={ds.data}
                        barRatio={0.8}
                        style={{ data: { fill: ds.color } }}
                        labelComponent={<VictoryTooltip flyoutStyle={{ fill: 'white', stroke: '#e5e7eb' }} style={{ fontSize: 9 }} />}
                        labels={({ datum }) => datum.y > 0 ? `${ds.role}: ${datum.y}` : ''}
                      />
                    ))}
                  </VictoryStack>
                </VictoryChart>
              </div>
            </div>
          );
        })()}

        {/* Day chart */}
        {activeChart !== 'week' && (() => {
          const { datasets, roleNames, hourLabel } = buildDayData(activeChart);
          const dayLabel = format(new Date(activeChart), 'EEEE, MMMM d yyyy');
          const maxY = Math.max(1, ...datasets.flatMap(ds => ds.data.map(d => d.y)));
          const activeHours = Array.from({ length: 24 }, (_, i) => i).filter(h =>
            datasets.some(ds => ds.data[h]?.y > 0)
          );
          const minHour = activeHours.length ? Math.max(0, activeHours[0] - 1) : 6;
          const maxHour = activeHours.length ? Math.min(23, activeHours[activeHours.length - 1] + 1) : 22;
          const visibleHours = Array.from({ length: maxHour - minHour + 1 }, (_, i) => minHour + i);
          const trimmedDatasets = datasets.map(ds => ({
            ...ds,
            data: ds.data.filter((_, i) => i >= minHour && i <= maxHour),
          }));
          const hasAny = datasets.some(ds => ds.data.some(d => d.y > 0));
          return (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Coverage on {dayLabel}</h3>
                <div className="flex flex-wrap gap-3">
                  {roleNames.map((r, i) => (
                    <span key={r} className="flex items-center gap-1 text-xs text-gray-600">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: ROLE_COLORS[i % ROLE_COLORS.length] }} />
                      {r}
                    </span>
                  ))}
                </div>
              </div>
              {!hasAny ? (
                <p className="text-xs text-gray-400 py-8 text-center">No shifts booked on this day.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <VictoryChart width={800} height={280} domainPadding={{ x: 20 }}>
                    <VictoryAxis
                      tickValues={visibleHours.map(h => hourLabel(h))}
                      style={{
                        tickLabels: { fontSize: 8, fill: '#6b7280', angle: -40, textAnchor: 'end' },
                        axis: { stroke: '#e5e7eb' },
                        grid: { stroke: 'none' },
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      domain={[0, maxY + 1]}
                      tickFormat={t => Number.isInteger(t) ? t : ''}
                      style={{
                        tickLabels: { fontSize: 9, fill: '#6b7280' },
                        axis: { stroke: '#e5e7eb' },
                        grid: { stroke: '#f3f4f6', strokeDasharray: '4' },
                      }}
                      label="Staff"
                    />
                    <VictoryStack>
                      {trimmedDatasets.map((ds, i) => (
                        <VictoryBar
                          key={i}
                          data={ds.data}
                          barRatio={0.8}
                          style={{ data: { fill: ds.color } }}
                          labelComponent={<VictoryTooltip flyoutStyle={{ fill: 'white', stroke: '#e5e7eb' }} style={{ fontSize: 9 }} />}
                          labels={({ datum }) => datum.y > 0 ? `${ds.role}: ${datum.y}` : ''}
                        />
                      ))}
                    </VictoryStack>
                  </VictoryChart>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── Draft Coverage Timeline (Pre-Publish) ─────────────────────── */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-sm font-bold text-gray-800">📝 Draft Coverage Timeline</h3>
          <span className="text-xs text-gray-400 italic">(inputs not yet published)</span>
        </div>
        {/* Day tab strip */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(() => {
            const startOfSelectedWeek = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7);
            return Array.from({ length: 7 }, (_, i) => {
              const d = addDays(startOfSelectedWeek, i);
              const dateStr = format(d, 'yyyy-MM-dd');
              const label = format(d, 'EEE d');
              const hasDraft = shiftDetails.some(sm => {
                const ds = sm.shifts.find(s => s.date === dateStr);
                return ds && ds.startTime && ds.endTime;
              });
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => setActiveDraftDay(dateStr)}
                  className={`relative px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    activeDraftDay === dateStr
                      ? 'bg-emerald-600 text-white shadow'
                      : 'bg-gray-100 text-gray-600 hover:bg-emerald-50'
                  }`}
                >
                  {label}
                  {hasDraft && (
                    <span className={`ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-bold ${
                      activeDraftDay === dateStr ? 'bg-white text-emerald-600' : 'bg-emerald-500 text-white'
                    }`}>✎</span>
                  )}
                </button>
              );
            });
          })()}
        </div>
        {/* Draft stacked area chart */}
        {activeDraftDay && (() => {
          const { datasets, roleNames, hourLabel, minHour, maxHour, hasAny } = buildDraftDayCoverage(activeDraftDay);
          const dayLabel = format(new Date(activeDraftDay), 'EEEE, MMMM d yyyy');
          const maxY = Math.max(1, ...datasets.flatMap(ds => ds.data.map(d => d.y)));
          const visibleHours = Array.from({ length: maxHour - minHour + 1 }, (_, i) => minHour + i);
          return (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Draft Coverage — {dayLabel}</h3>
                <div className="flex flex-wrap gap-3">
                  {roleNames.map((r, i) => (
                    <span key={r} className="flex items-center gap-1 text-xs text-gray-600">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: ROLE_COLORS[i % ROLE_COLORS.length] }} />
                      {r}
                    </span>
                  ))}
                </div>
              </div>
              {!hasAny ? (
                <p className="text-xs text-gray-400 py-8 text-center">No draft shifts entered for this day yet — use the time inputs above to see coverage.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <VictoryChart width={800} height={260} domainPadding={{ x: 20 }}>
                    <VictoryAxis
                      tickValues={visibleHours}
                      tickFormat={h => hourLabel(h)}
                      style={{
                        tickLabels: { fontSize: 8, fill: '#6b7280', angle: -40, textAnchor: 'end' },
                        axis: { stroke: '#e5e7eb' },
                        grid: { stroke: 'none' },
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      domain={[0, maxY + 1]}
                      tickFormat={t => Number.isInteger(t) ? t : ''}
                      style={{
                        tickLabels: { fontSize: 9, fill: '#6b7280' },
                        axis: { stroke: '#e5e7eb' },
                        grid: { stroke: '#f3f4f6', strokeDasharray: '4' },
                      }}
                      label="Staff"
                    />
                    <VictoryStack>
                      {datasets.map((ds, i) => (
                        <VictoryBar
                          key={i}
                          data={ds.data}
                          barRatio={0.8}
                          style={{ data: { fill: ds.color } }}
                          labelComponent={<VictoryTooltip flyoutStyle={{ fill: 'white', stroke: '#e5e7eb' }} style={{ fontSize: 9 }} />}
                          labels={({ datum }) => datum.y > 0 ? `${ds.role}: ${datum.y}` : ''}
                        />
                      ))}
                    </VictoryStack>
                  </VictoryChart>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ShiftBooking;